// src/components/AntiVpnGuard.jsx
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { checkIpVpnStatus } from '../services/vpnService';

import { checkMultiAccount } from '../services/multiAccountDetector';
import { getEnhancedFingerprintWithMeta } from '../fingerprint';
import { FiShieldOff, FiAlertTriangle, FiRefreshCw, FiUserX, FiGlobe, FiLock } from 'react-icons/fi';

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(5, 5, 10, 0.88)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    animation: 'fadeIn 0.3s ease-out',
};

const cardStyle = {
    background: 'linear-gradient(135deg, rgba(20, 22, 35, 0.95) 0%, rgba(12, 14, 24, 0.98) 100%)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    boxShadow: '0 20px 50px rgba(239, 68, 68, 0.25), 0 0 30px rgba(0, 0, 0, 0.8)',
    borderRadius: '24px',
    maxWidth: '520px',
    width: '100%',
    padding: '36px 28px',
    textAlign: 'center',
    color: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
};

const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.4)',
    color: '#f87171',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '700',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '16px',
};

export const AntiVpnGuard = ({ children, activeUser, strictMode = true }) => {
    const [vpnDetected, setVpnDetected] = useState(false);
    const [multiAccountDetected, setMultiAccountDetected] = useState(false);
    const [multiAccountDetails, setMultiAccountDetails] = useState(null);
    const [ipDetails, setIpDetails] = useState(null);
    const [checking, setChecking] = useState(true);
    const [rechecking, setRechecking] = useState(false);

    const runSecurityCheck = async (forceRefresh = false) => {
        setChecking(true);
        if (forceRefresh) {
            try { sessionStorage.removeItem('vpn_check_cache_v2'); } catch (_) {}
        }
        try {
            // 1. Detección de VPN por IP & Datacenter API
            const ipStatus = await checkIpVpnStatus();
            setIpDetails(ipStatus);

            // 2. Detección por WebRTC & Heurística del cliente
            const metaFp = await getEnhancedFingerprintWithMeta();

            const isVpnActive = ipStatus.allowed === false || ipStatus.isVpn || ipStatus.isProxy || ipStatus.isHosting || ipStatus.isTor || metaFp.vpnSuspected;

            console.log('🔒 [AntiVpnGuard Status]:', {
                isVpnActive,
                allowed: ipStatus.allowed,
                ip: ipStatus.ip,
                country: ipStatus.country,
                reasons: ipStatus.reasons,
                vpnRiskScore: ipStatus.vpnRiskScore,
                riskLevel: ipStatus.riskLevel,
                vpnSuspectedWebRTC: metaFp.vpnSuspected
            });


            setVpnDetected(isVpnActive);

            // 3. Detección Anti-Multicuentas si hay usuario activo
            const kickUser = activeUser || localStorage.getItem('kick_user');
            if (kickUser) {
                const multiCheck = await checkMultiAccount(kickUser);
                if (multiCheck.isMultiAccount) {
                    setMultiAccountDetected(true);
                    setMultiAccountDetails(multiCheck);
                } else {
                    setMultiAccountDetected(false);
                }
            }
        } catch (err) {
            console.error('Error en verificación AntiVpnGuard:', err);
        } finally {
            setChecking(false);
            setRechecking(false);
        }
    };

    const location = useLocation();

    useEffect(() => {
        runSecurityCheck(true);

        const handleWindowFocus = () => {
            runSecurityCheck(true);
        };

        window.addEventListener('focus', handleWindowFocus);
        document.addEventListener('visibilitychange', handleWindowFocus);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
            document.removeEventListener('visibilitychange', handleWindowFocus);
        };
    }, [activeUser, location.pathname]);


    const handleRecheck = () => {
        setRechecking(true);
        runSecurityCheck(true);
    };


    // Si el modo estricto no está activo y no hay infracciones graves, renderizar contenido normal
    const isBlocked = strictMode && (vpnDetected || multiAccountDetected);

    return (
        <>
            {isBlocked && (
                <div style={modalOverlayStyle}>
                    <div style={cardStyle}>
                        {vpnDetected ? (
                            <>
                                <div style={{ fontSize: '3rem', color: '#ef4444', marginBottom: '12px' }}>
                                    <FiShieldOff />
                                </div>
                                <div style={badgeStyle}>
                                    <FiLock /> VPN / Proxy Detectado
                                </div>
                                <h2 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '12px', color: '#fff' }}>
                                    Acceso Restringido por VPN
                                </h2>
                                <p style={{ fontSize: '0.95rem', color: '#9ca3af', lineHeight: '1.6', marginBottom: '20px' }}>
                                    Hemos detectado que estás navegando mediante una <strong>VPN, Proxy o Red de Datacenter</strong>.
                                    Para mantener la transparencia y prevenir multicuentas, el uso de VPN está estrictamente prohibido en nuestra plataforma.
                                </p>

                                {ipDetails && (
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '12px',
                                        padding: '12px 16px',
                                        fontSize: '0.82rem',
                                        color: '#d1d5db',
                                        textAlign: 'left',
                                        marginBottom: '24px',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                            <FiGlobe style={{ color: '#60a5fa' }} /> <strong>IP Detectada:</strong> {ipDetails.ip || 'Oculta'}
                                        </div>
                                        {ipDetails.isp && (
                                            <div style={{ marginBottom: '4px' }}>
                                                <strong>Proveedor/ISP:</strong> {ipDetails.isp}
                                            </div>
                                        )}
                                        {ipDetails.reasons && ipDetails.reasons.length > 0 && (
                                            <div style={{ color: '#f87171', marginTop: '6px' }}>
                                                <strong>Motivo:</strong> {ipDetails.reasons.join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={handleRecheck}
                                    disabled={rechecking}
                                    style={{
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px 24px',
                                        fontSize: '1rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <FiRefreshCw style={{ animation: rechecking ? 'spin 1s linear infinite' : 'none' }} />
                                    {rechecking ? 'Reverificando...' : 'Desconectar VPN y Reverificar'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: '3rem', color: '#f59e0b', marginBottom: '12px' }}>
                                    <FiUserX />
                                </div>
                                <div style={{ ...badgeStyle, backgroundColor: 'rgba(245, 158, 11, 0.15)', borderColor: 'rgba(245, 158, 11, 0.4)', color: '#fbbf24' }}>
                                    <FiAlertTriangle /> Multicuenta Detectada
                                </div>
                                <h2 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '12px', color: '#fff' }}>
                                    Dispositivo Bloqueado
                                </h2>
                                <p style={{ fontSize: '0.95rem', color: '#9ca3af', lineHeight: '1.6', marginBottom: '20px' }}>
                                    Se ha detectado que este dispositivo físico ya ha sido utilizado para participar o autenticarse con otra cuenta de Kick.
                                    Solo se permite <strong>1 cuenta por persona y por dispositivo</strong>.
                                </p>
                                {multiAccountDetails && (
                                    <div style={{
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        borderRadius: '12px',
                                        padding: '12px 16px',
                                        fontSize: '0.85rem',
                                        color: '#fcd34d',
                                        textAlign: 'center',
                                        marginBottom: '24px',
                                        border: '1px solid rgba(245, 158, 11, 0.25)',
                                    }}>
                                        {multiAccountDetails.reason}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {children}
        </>
    );
};

export default AntiVpnGuard;
